import { useMap } from 'react-leaflet';
import type { LatLngBoundsExpression } from 'leaflet';

interface MapZoomProps {
  /**
   * Default geographic bounds the map should snap to when the user
   * clicks the Reset view button. Passed down from `LeafletMap` so
   * the same bounds drive the initial fit and the manual reset.
   */
  readonly defaultBounds?: LatLngBoundsExpression;
}

/**
 * Custom map control stack — replaces Leaflet's default
 * `.leaflet-control-zoom` (hidden via globals.css). Three stacked
 * buttons: zoom-in (+), zoom-out (−), and a Reset view button
 * added in the 2026-05-18 feedback pass.
 *
 * The Reset button fits the map to `defaultBounds` (DEFAULT_FIT_BOUNDS
 * from LeafletMap — tight contiguous US) so users can always recover
 * the default view, even if they've zoomed deep or panned off
 * the data. The wider AK/HI envelope is only used as the pan envelope,
 * not as the reset target (see LeafletMap.tsx bounds comment).
 *
 * Sits inside `<MapContainer>` (must be a child of it) so
 * `useMap()` can reach the underlying Leaflet instance.
 */
export function MapZoom({ defaultBounds }: MapZoomProps = {}) {
  const map = useMap();

  const handleReset = () => {
    // Reset = "back to the default US view". Close any open metro popup
    // too, so the map truly returns to its neutral state — this also
    // fires Leaflet's `popupclose`, which the page listens for to flip
    // its `isPopupOpen` flag off and bring the (popup-hidden) legend back.
    map.closePopup();
    if (defaultBounds) {
      // Keep these options in sync with DEFAULT_FIT_OPTIONS in LeafletMap.tsx
      // — same padding [10, 10] + maxZoom 5 so Reset View matches the
      // initial-load framing exactly. Tightened on 2026-05-25 follow-up
      // to PR #49.
      map.fitBounds(defaultBounds, { padding: [10, 10], maxZoom: 5 });
    } else {
      map.setView([38.5, -96], 4);
    }
  };

  return (
    <div
      role="group"
      aria-label="Map controls"
      className="absolute right-4 top-4 z-[400] flex flex-col overflow-hidden rounded-lg border border-border bg-white shadow-panel"
    >
      <button
        type="button"
        aria-label="Zoom in"
        onClick={() => map.zoomIn()}
        className="flex h-9 w-9 items-center justify-center border-b border-border bg-white font-sans text-lg font-bold text-navy transition-colors hover:bg-ice"
      >
        +
      </button>
      <button
        type="button"
        aria-label="Zoom out"
        onClick={() => map.zoomOut()}
        className="flex h-9 w-9 items-center justify-center border-b border-border bg-white font-sans text-lg font-bold text-navy transition-colors hover:bg-ice"
      >
        −
      </button>
      <button
        type="button"
        aria-label="Reset view to default"
        title="Reset view"
        onClick={handleReset}
        className="flex h-9 w-9 items-center justify-center bg-white text-navy transition-colors hover:bg-ice"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
          <polyline points="9 21 9 12 15 12 15 21" />
        </svg>
      </button>
    </div>
  );
}
